/**
 * 기관코드별 사이드바 로고
 */
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';

export const TENANT_LOGO_STORAGE_KEY = 'erp.tenantLogos';
export const TENANT_LOGO_CHANGED_EVENT = 'erp-tenant-logo-changed';

/** dataURL 대략 상한 (localStorage 용량 보호) */
export const TENANT_LOGO_MAX_BYTES = 450 * 1024;

/**
 * @typedef {{
 *   logoDataUrl: string,
 *   logoAlt?: string,
 *   logoAreaDark?: boolean,
 *   updatedAt?: string,
 * }} TenantLogoEntry
 */

function normalizeCode(compCd) {
  return String(compCd || '').trim().toUpperCase();
}

/** @returns {Record<string, TenantLogoEntry>} */
export function loadAllTenantLogos() {
  try {
    const raw = localStorage.getItem(TENANT_LOGO_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
}

/** @param {string} compCd */
export function loadTenantLogo(compCd) {
  const code = normalizeCode(compCd);
  if (!code) return null;
  const entry = loadAllTenantLogos()[code];
  if (!entry?.logoDataUrl) return null;
  return { ...entry, tenantCode: code };
}

/**
 * @param {Record<string, TenantLogoEntry>} map
 */
export function saveAllTenantLogos(map) {
  const next = map && typeof map === 'object' ? map : {};
  localStorage.setItem(TENANT_LOGO_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TENANT_LOGO_CHANGED_EVENT));
  }
  return next;
}

/**
 * @param {string} compCd
 * @param {{ logoDataUrl: string, logoAlt?: string, logoAreaDark?: boolean }} payload
 */
export function saveTenantLogo(compCd, payload) {
  const code = normalizeCode(compCd);
  if (!code) return { ok: false, message: '기관코드를 입력하세요.' };
  const dataUrl = String(payload?.logoDataUrl || '');
  if (!dataUrl.startsWith('data:image/')) {
    return { ok: false, message: '이미지 파일이 필요합니다.' };
  }
  if (dataUrl.length > TENANT_LOGO_MAX_BYTES) {
    return { ok: false, message: '이미지 용량이 너무 큽니다. 450KB 이하로 올려 주세요.' };
  }
  const all = loadAllTenantLogos();
  all[code] = {
    logoDataUrl: dataUrl,
    logoAlt: String(payload?.logoAlt || '').trim() || code,
    logoAreaDark: payload?.logoAreaDark === true,
    updatedAt: new Date().toISOString(),
  };
  saveAllTenantLogos(all);
  if (isBdApiEnabled()) {
    enqueuePersist(`logo|${code}`, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.TENANT_LOGO,
        rowUid: code,
        payload: all[code],
        params: pocParams({ tenantCode: code }),
      })
    );
  }
  return { ok: true, entry: all[code] };
}

/** @param {string} compCd */
export function removeTenantLogo(compCd) {
  const code = normalizeCode(compCd);
  if (!code) return { ok: false, message: '기관코드를 입력하세요.' };
  const all = loadAllTenantLogos();
  if (!all[code]) return { ok: true };
  delete all[code];
  saveAllTenantLogos(all);
  return { ok: true };
}

/**
 * File → dataURL
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readImageFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      reject(new Error('이미지 파일만 업로드할 수 있습니다.'));
      return;
    }
    if (file.size > TENANT_LOGO_MAX_BYTES) {
      reject(new Error('파일 용량이 450KB를 초과합니다.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다.'));
    reader.readAsDataURL(file);
  });
}
